import { IContentItem } from '@kontent-ai/delivery-sdk';
import { FormattedAccordion } from 'components/Accordion';
import { FormattedConvenienceVariables } from 'components/ConvenienceVariables';
import {
  Accordion,
  Audio,
  Branch,
  ComparisonChoiceSection,
  ContentLibraryCard,
  FormCheckmarks,
  FormSelects,
  LibraryPage,
  Tooltip,
  ValidationMessage,
  Video,
} from 'kontent-ai/content-types';
import { Asset, FormattedAudio, FormattedVideo } from 'types/kontentAi';
import { PlanValue } from 'kontent-ai/content-types/plan_value';
import { DestinationPage } from 'lib/content-parsers/destinationPage';
import { Condition, ContentCardItem, Destination } from 'types/content';
import { PlanValueText } from 'types/findAPlan';
import { Page } from 'types/pages';
import conditionMap from 'utils/conditions';

export const parseConditions = (conditions: IContentItem[]): Condition[] => {
  return conditions.map((condition) => {
    if (condition.system.type === 'code_condition') {
      return conditionMap[condition.system.codename] as Condition;
    }

    return {
      expectedValue: condition.elements.expectedValue.value,
      operator: condition.elements.operator.value[0].name,
      variableName: condition.elements.variableName.value,
    } as Condition;
  });
};

export function parseContentCards(cards: LibraryPage[]): ContentCardItem[] {
  return cards?.map((card) => ({
    body: card?.elements?.body?.value,
    filterKey: card?.elements?.filterKey?.value || '',
    gatedConditions: card?.elements?.gatedConditions?.value || null,
    gatedConditionText: card?.elements?.gatedConditionText?.value,
    gatedContentButtonDestination:
      card?.elements?.gatedContentButtonDestination?.linkedItems[0]?.elements
        ?.slug?.value,
    icon: card.elements?.icons?.linkedItems[0]?.elements?.code?.value,
    linkText: card?.elements?.linkText?.value,
    slug: card?.elements?.slug?.value,
    title: card?.elements?.title?.value,
  }));
}

export function parseLandingPageContentCards(cards: ContentLibraryCard[]) {
  return cards.map((card) => {
    const libraryPage = card.elements.libraryPage.linkedItems[0] as LibraryPage;
    if (card?.elements?.icons?.linkedItems?.length > 0) {
      return {
        conditions:
          parseConditions(card.elements.condition.linkedItems) || null,
        filterKey: libraryPage.elements.filterKey?.value || '',
        gatedConditions: card.elements.gatedConditions.value,
        gatedConditionText: card.elements.gatedConditionText.value,
        gatedContentButtonDestination:
          card.elements.gatedContentButtonDestination?.linkedItems[0]?.elements
            ?.slug?.value || '/',
        icon: card.elements.icons.linkedItems[0].elements.code.value,
        slug: libraryPage.elements.slug.value,
        title: card.elements.title.value,
      };
    }

    // TODO: Remove this once all cards have been migrated

    return {
      conditions: parseConditions(card.elements.condition.linkedItems) || null,
      filterKey: libraryPage.elements.filterKey?.value || '',
      gatedConditions: libraryPage.elements.gatedConditions.value,
      gatedConditionText: libraryPage.elements.gatedConditionText.value,
      gatedContentButtonDestination:
        libraryPage.elements.gatedContentButtonDestination?.linkedItems[0]
          ?.elements?.slug?.value,
      icon: libraryPage.elements.icons.linkedItems[0].elements.code.value,
      slug: libraryPage.elements.slug.value,
      title: libraryPage.elements.title.value,
    };
  });
}

export const parseDestinations = (
  items: 'back' | DestinationPage[]
): Destination[] => {
  // If were using the input choice back pass back as a destination
  if (items === 'back') {
    return [
      {
        conditions: [],
        slug: 'back',
      },
    ];
  }
  return items.map((item) => {
    // More processing for a branch
    if (item.system.type === 'branch') {
      item = item as Branch;

      const linkedPage = item.elements.destination.linkedItems[0];
      const slug = linkedPage?.elements?.slug.value;

      return {
        conditions: parseConditions(item.elements.conditions.linkedItems),
        slug,
      };
    }

    // else just hit us with the destination
    item = item as Page;
    return {
      conditions: [],
      slug: item.elements.slug?.value,
    };
  });
};

export function parseAccordion(accordion: Accordion): FormattedAccordion {
  const libraryPages =
    accordion?.elements?.libraryPage?.linkedItems?.map((card) => ({
      slug: card?.elements?.slug?.value,
    })) || [];

  return {
    categoryTag: accordion?.elements?.categoryTag?.value[0]?.name,
    externalCtaText: accordion?.elements?.externalCtaText?.value || '',
    externalCtaUrl: accordion?.elements?.externalCtaUrl?.value || '',
    filterKey: accordion?.elements?.filterKey?.value || '',
    headline: accordion?.elements?.headline?.value,
    libraryPageSlug: libraryPages[0]?.slug || '',
    summary: accordion?.elements?.summary?.value,
  };
}

export function parseAudio(audio: Audio): FormattedAudio {
  return {
    closedCaption: (audio?.elements?.closedcaption?.value[0] as Asset) || null,
    mp3File: (audio?.elements?.mp3File?.value[0] as Asset) || null,
    mp3Url: audio?.elements?.mp3url?.value,
  };
}

export function parseVideo(video: Video): FormattedVideo {
  return {
    closedCaption: video?.elements?.closedcaption?.value[0] as Asset,
    dynamicText: (video?.elements?.dynamicText?.value[0] as Asset) || null,
    mp4File: (video?.elements?.mp4File?.value[0] as Asset) || null,
    mp4Url: video?.elements?.mp4url?.value,
    videoStillImage: video?.elements?.videostillimage?.value[0] as Asset,
    videoTranscript: video?.elements?.videotranscript?.value,
  };
}

export const parseFormOptions = (options: FormCheckmarks[] | FormSelects[]) =>
  options.map((option: FormSelects) => ({
    key: option.elements.selecttext.value,
    label: option.elements.selecttext.value,
    value: option.elements.selectvalue.value,
  }));

export const parseValidationMessage = (question: ValidationMessage) =>
  question?.elements?.text?.value || '';

export const sortSections = (
  sections: ComparisonChoiceSection[],
  sectionSort: string[]
) => {
  return sections.sort((section, nextSection) => {
    return (
      sectionSort.indexOf(section.system.codename) -
      sectionSort.indexOf(nextSection.system.codename)
    );
  });
};

export const parseConvenienceVariables = (): FormattedConvenienceVariables => {
  return {
    partnerName: 'Lig',
    partnerPhone: '(833) 748-5302',
  };
};

export const parsePlanValues = (items: PlanValue[]): PlanValueText => {
  return items.reduce((result: Record<string, string>, item) => {
    result[item?.elements?.key?.value] = item?.elements?.label?.value;
    return result;
  }, {});
};

export const parseTooltips = (items: Tooltip[]) => {
  const tooltips: Record<string, string> = {};

  items.forEach((item) => {
    tooltips[item.elements.name.value] = item.elements.tooltip.value;
  });

  return tooltips;
};
